import { cn } from "@/lib/cn";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

export interface FilterItem {
  label: string;
  count?: number;
  active?: boolean;
  onClick: () => void;
}

export interface FilterGroup {
  title: string;
  items: FilterItem[];
}

interface Props {
  groups: FilterGroup[];
  /** Called when "Clear" is clicked in the header. */
  onClear?: () => void;
  className?: string;
}

export function FilterSidebar({ groups, onClear, className }: Props) {
  return (
    <aside className={cn("w-56 shrink-0 border-r bg-card", className)}>
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Filter by</span>
        {onClear && (
          <button onClick={onClear} className="text-xs text-muted-foreground hover:text-foreground">
            Clear
          </button>
        )}
      </div>
      <div className="py-1">
        {groups.map((g) => <Group key={g.title} group={g} />)}
      </div>
    </aside>
  );
}

function Group({ group }: { group: FilterGroup }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="text-xs font-semibold">{group.title}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", !open && "-rotate-90")} />
      </button>
      {open && (
        <ul className="pb-1">
          {group.items.map((it) => (
            <li key={it.label}>
              <button
                onClick={it.onClick}
                className={cn(
                  "flex w-full items-center justify-between px-4 py-1.5 text-left text-sm",
                  it.active ? "bg-primary/10 text-primary font-medium" : "text-foreground/80 hover:bg-muted",
                )}
              >
                <span>{it.label}</span>
                {it.count !== undefined && (
                  <span className={cn("rounded-full px-1.5 text-xs", it.active ? "bg-primary/20" : "bg-muted")}>
                    {it.count}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
